import { useState } from "react";
import toast from "react-hot-toast";
import { Building2, Plus, X } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { useOrganizations, useCreateOrganization, ORGANIZATION_STATUS_LABEL, type OrganizationRecord } from "@/api/organizations";
import { useIndustries, useOrganizationModules, useSetOrganizationModules } from "@/api/modules";
import { getApiErrorMessage } from "@/api/client";

/**
 * Administración de organizaciones (tenants) — exclusivo de SUPER_ADMIN
 * (petición explícita del usuario, sección 26 del "PROMPT DE INTEGRACIÓN":
 * "crear empresa, editar empresa, seleccionar industria, activar/
 * desactivar módulos"). Cada organización nueva es un tenant 100% aislado
 * (mismo mecanismo que ya usa cada cliente contable hoy).
 */
export default function OrganizationsPage() {
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.roles.includes("SUPER_ADMIN") ?? false;
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<OrganizationRecord | null>(null);

  const { data: organizations, isLoading } = useOrganizations(isSuperAdmin);

  if (!isSuperAdmin) {
    return (
      <div className="card p-6 text-sm text-gray-500">Esta sección es exclusiva de administradores del sistema (SUPER_ADMIN).</div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Organizaciones</h1>
          <p className="text-sm text-gray-500">Empresas (tenants) que usan la plataforma — cada una completamente aislada de las demás.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} className="mr-1.5" /> Nueva organización
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Organización</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Código</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Módulos</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {organizations?.map((org) => (
              <tr key={org.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-900">
                  <span className="flex items-center gap-2">
                    <Building2 size={14} className="text-gray-400" /> {org.name}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600">{org.slug}</td>
                <td className="px-4 py-3 text-gray-600">{ORGANIZATION_STATUS_LABEL[org.status] ?? org.status}</td>
                <td className="px-4 py-3 text-right">
                  <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => setSelected(org)}>
                    Configurar módulos
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateOrganizationModal onClose={() => setShowCreate(false)} />}
      {selected && <OrganizationModulesModal organization={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function CreateOrganizationModal({ onClose }: { onClose: () => void }) {
  const { data: industries } = useIndustries(true);
  const createOrg = useCreateOrganization();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [email, setEmail] = useState("");
  const [industryId, setIndustryId] = useState("");
  const [adminFullName, setAdminFullName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");

  const isValid = name.trim().length >= 2 && /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug) && adminFullName.trim() && adminEmail.trim() && adminPassword.length >= 10;

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error("Complete todos los campos requeridos (la contraseña del administrador debe tener al menos 10 caracteres).");
      return;
    }
    try {
      await createOrg.mutateAsync({
        name: name.trim(),
        slug: slug.trim(),
        email: email.trim() || undefined,
        industryId: industryId || undefined,
        adminUser: { fullName: adminFullName.trim(), email: adminEmail.trim(), password: adminPassword },
      });
      toast.success("Organización creada correctamente.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Nueva organización</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[75vh] space-y-3 overflow-y-auto p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Nombre</label>
            <input className="input-field mt-1" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Código (slug)</label>
            <input className="input-field mt-1" placeholder="mi-tienda" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Correo (opcional)</label>
            <input className="input-field mt-1" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Industria</label>
            <select className="input-field mt-1" value={industryId} onChange={(e) => setIndustryId(e.target.value)}>
              <option value="">Sin clasificar (elegir módulos manualmente después)</option>
              {industries?.map((ind) => (
                <option key={ind.id} value={ind.id}>
                  {ind.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-400">Precarga los módulos típicos de esa industria. Se pueden ajustar después.</p>
          </div>
          <div className="border-t border-gray-100 pt-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Administrador inicial</p>
            <div className="mt-2 space-y-2">
              <input className="input-field" placeholder="Nombre completo" value={adminFullName} onChange={(e) => setAdminFullName(e.target.value)} />
              <input className="input-field" placeholder="Correo" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} />
              <input
                className="input-field"
                type="password"
                placeholder="Contraseña (mínimo 10 caracteres)"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={!isValid || createOrg.isPending} onClick={handleSubmit}>
            {createOrg.isPending ? "Creando..." : "Crear organización"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrganizationModulesModal({ organization, onClose }: { organization: OrganizationRecord; onClose: () => void }) {
  const { data: modules, isLoading } = useOrganizationModules(organization.id);
  const setModules = useSetOrganizationModules(organization.id);
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const grouped = new Map<string, typeof modules>();
  for (const m of modules ?? []) {
    const key = m.navGroup ?? "General";
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(m);
  }

  const toggle = (moduleId: string, current: boolean) => setPending((prev) => ({ ...prev, [moduleId]: !current }));

  const handleSave = async () => {
    const updates = Object.entries(pending).map(([moduleId, enabled]) => ({ moduleId, enabled }));
    if (updates.length === 0) {
      onClose();
      return;
    }
    try {
      await setModules.mutateAsync(updates);
      toast.success("Módulos actualizados.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[80vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Módulos de {organization.name}</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
          {Array.from(grouped.entries()).map(([groupLabel, mods]) => (
            <div key={groupLabel}>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">{groupLabel}</p>
              <div className="space-y-1">
                {mods?.map((m) => {
                  const enabled = pending[m.moduleId] ?? m.enabled;
                  return (
                    <label key={m.moduleId} className="flex items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-gray-50">
                      <span className="text-gray-700">{m.name}</span>
                      <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-brand-600" checked={enabled} onChange={() => toggle(m.moduleId, enabled)} />
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={setModules.isPending} onClick={handleSave}>
            {setModules.isPending ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
